import { computed, defineComponent, reactive, ref } from 'vue';
import { useSweetAlert } from '@/composables/sweetalert';
import { useStore } from 'vuex';
import { SupportAction, SupportGetter } from '@/store/support/support.module';
import { SupportSchema } from '@/store/support/support.validator';
import { Support } from '@/store/support/support.model';
import BaseModalComponent from '@/components/baseModal/BaseModal.vue';

export default defineComponent({
  name: 'SupportFormComponent',
  components: {
    BaseModalComponent
  },
  props: {
    supportId: {
      type: Number
    },
  },
  setup: (props) => {
    const store = useStore();
    const swal = useSweetAlert();
    const fileDialog = ref();
    const isEditMode = computed(() => !!props.supportId);
    const support = computed<Support | undefined>(() => store.getters[SupportGetter.BY_ID](props.supportId));
    const title = computed(() => isEditMode.value ? 'แก้ไขหนังสือข้อมูลสนับสนุน' : 'เพิ่มหนังสือข้อมูลสนับสนุน');
    const supportForm = ref<InstanceType<typeof BaseModalComponent>>();

    // form
    interface Form {
      id?: number | null;
      title?: string | null;
      pined?: boolean;
      file?: string | null;
    }
    const formData = reactive<Form>({
      id: null,
      title: '',
      pined: false,
      file: '',
    });
    const validator = computed(() => SupportSchema.validate(formData, { abortEarly: false }).error);
    const errors = reactive({
      title: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'title')),
      file: computed(() => validator.value?.details.some((detail) => detail.path[0] === 'file')),
    });
    const isValid = computed(() => !errors.title && !errors.file);
    const isLoading = computed(() => store.getters[SupportGetter.STATUS] === 'init' || store.getters[SupportGetter.STATUS] === 'loading');
    const submited = ref(false);

    // show modal
    const show = () => {
      new Promise<void>((resolve) => {
        formData.id = (isEditMode.value) ? props.supportId : null;
        formData.title = support.value?.title;
        formData.pined = (isEditMode.value) ? support.value?.pined : false;
        formData.file = support.value?.file;
        resolve();
      }).then(() => supportForm.value?.show());
    };

    // hide modal
    const hide = () => {
      supportForm.value?.hide();
    };

    // change file
    const fileChange = (event: Event) => {
      const files = (event.target as HTMLInputElement).files;
      const file = files?.item(0);

      if (file) {
        formData.file = file.name;
      }
    };

    // clear submit status
    const unsubmit = () => {
      submited.value = false;
    };

    // clear form
    const clearForm = () => {
      new Promise<void>((resolve) => {
        formData.id = null;
        formData.title = '';
        formData.pined = false;
        formData.file = '';
        fileDialog.value.value = null;
        resolve();
      }).then(() => unsubmit());
    };

    // submit form
    const submitClick = () => {
      submited.value = true;

      if (isValid.value) {
        if (isEditMode.value) {
          store.dispatch(SupportAction.UPDATE, {
            id: formData.id,
            title: formData.title,
            pined: formData.pined,
            file: (fileDialog.value?.files?.length !== 0) ? fileDialog.value?.files?.item(0) as File : null,
          }).then(() => hide());
        } else {
          store.dispatch(SupportAction.CREATE, {
            title: formData.title,
            pined: formData.pined,
            file: fileDialog.value?.files?.item(0) as File,
          }).then(() => hide());
        }
      }
    };

    // delete item
    const deleteClick = () => {
      swal.confirm('ลบข้อมูล!', 'คุณต้องการลบข้อมูลใช่หรือไม่?', 'ใช่ ลบเดี๋ยวนี้!', '#dc3545')
        .then((result) => {
          if (result.isConfirmed) {
            store.dispatch(SupportAction.DELETE, formData)
              .then(() => hide());
          }
        });
    };

    return {
      title,
      formData,
      supportForm,
      isEditMode,
      fileDialog,
      errors,
      submited,
      isLoading,
      show,
      hide,
      fileChange,
      unsubmit,
      clearForm,
      submitClick,
      deleteClick,
    };
  },
});
